(ns ui.view.transaction-process.cards.details-modal
  (:require
   [common.localization :refer [tr]]
   [re-frame.core :as rf]
   [ui.components.modal.view :as modal]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.transaction-process.components.attachment-row :as attachment-row]
   [ui.view.transaction-process.components.sub-entity-display :as sub-display]
   [ui.view.transaction-process.util :as tp-util]))

(defn- field-display
  [{:keys [label value]}]
  [:div
   [:div {:class "text-xs m-0 text-gray-500"} label]
   [:div {:class "text-sm m-0 font-medium text-gray-900"} (or value "-")]])

;; Identity fields shown first in the details grid. short-name is a template field;
;; vehicle (ref) and status (enum) live in the schema but not the template layout —
;; summary-field-display resolves all three the same template-or-schema way as the
;; sidebar. They're excluded from the per-stage loop below to avoid rendering twice.
(def ^:private details-header-fields
  [:transaction-process/short-name
   :transaction-process/vehicle
   :transaction-process/status])

(defn- stage-field-groups
  "Per-stage simple-field cards for the details grid: walk stages → field-cards,
  keeping each card's visible, non-attachment, non-identity fields in template order.
  No field repeats across stages in the CapMan templates, so no dedup is needed; the
  layout/visibility therefore mirror the form's preparation/closing stages."
  [{:keys [template entity-data]}]
  (let [fields-config (:template/fields template)
        identity-set  (set details-header-fields)
        keep-field?   (fn [fk]
                        (let [fc (get fields-config fk)]
                          (and (not (identity-set fk))
                               (not (tp-util/attachment-slot-field? fc))
                               (tp-util/field-visible? {:field-config fc
                                                        :value-lookup entity-data}))))]
    (->> (:template/stages template)
         (keep (fn [stage]
                 (let [cards (->> (:stage/cards stage)
                                  (filter #(= :field-card (:card/type %)))
                                  (keep (fn [card]
                                          (let [fields (filterv keep-field? (:card/fields card))]
                                            (when (seq fields)
                                              {:card-label (:card/label card) :fields fields}))))
                                  vec)]
                   (when (seq cards)
                     {:stage-label (:stage/label stage) :cards cards}))))
         vec)))

(defn- details-section
  [{:keys [entity-data template]}]
  (let [fields-config (:template/fields template)
        id->entity    @(rf/subscribe [::session-sub/legacy-entities])
        identity-rows (->> details-header-fields
                           (mapv (fn [fk]
                                   (let [{:keys [label value]} (tp-util/summary-field-display
                                                                {:field-kw      fk
                                                                 :entity        entity-data
                                                                 :field-configs fields-config
                                                                 :id->entity    id->entity})]
                                     {:label (if (= fk :transaction-process/short-name) "Transaction name" label)
                                      :value value}))))
        stage-groups  (stage-field-groups {:template template :entity-data entity-data})]
    [:div {:id "details-section-details" :class "mb-8"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900 mb-3"} (tr :transaction-process/transaction-details)]
     [:div {:class "bg-white rounded-lg border border-gray-200 p-6"}
      [:div {:class "grid grid-cols-2 gap-x-8 gap-y-4 mb-6"}
       (doall
        (for [{:keys [label value]} identity-rows]
          ^{:key label}
          [field-display {:label label :value value}]))]
      (doall
       (for [{:keys [stage-label cards]} stage-groups]
         ^{:key stage-label}
         [:div {:class "mb-6 last:mb-0"}
          [:h4 {:class "text-sm m-0 font-semibold text-gray-700 mb-3"} stage-label]
          (doall
           (for [{:keys [card-label fields]} cards]
             ^{:key card-label}
             [:div {:class "mb-4 last:mb-0"}
              [:h5 {:class "text-xs m-0 font-medium text-gray-500 uppercase tracking-wider mb-2"} card-label]
              [:div {:class "grid grid-cols-2 gap-x-8 gap-y-4"}
               (doall
                (for [fk fields]
                  (let [fc (get fields-config fk)]
                    ^{:key fk}
                    [field-display {:label (or (:field/label fc)
                                               [tp-util/render-error
                                                {:context (str "Missing :field/label for " fk)
                                                 :value fk}])
                                    :value (tp-util/format-field-value {:field-kw    fk
                                                                        :field-cfg   fc
                                                                        :entity-data entity-data})}])))]]))]))]]))

(defn- decision-detail-card
  [{:keys [entry decision template]}]
  (let [{:keys [state label variant]} (tp-util/decision-status decision)
        logged? (= :logged state)
        slot-template (:slot/template entry)]
    [:div {:class (uu/cm "border rounded-lg p-4"
                         (cond
                           logged?
                           "border-primary-regular/30 bg-primary-regular/5"
                           (= :draft state)
                           "border-yellow-200 bg-yellow-50/50"
                           :else
                           "border-gray-200"))}
     [:div {:class "flex items-center justify-between mb-2"}
      [:span {:class "text-sm m-0 font-semibold text-gray-900"} (:slot/label entry)]
      (if decision
        [tp-util/status-badge {:label label :variant variant}]
        [:span {:class "text-xs m-0 text-gray-400 italic"} (tr :pending)])]
     (when decision
       (sub-display/value-field-rows {:slot-template slot-template :category :decisions
                                      :entity decision :template template}))
     [sub-display/conditions-list {:entity           decision
                                   :conditions-field :decision/conditions
                                   :can-write?       false
                                   :show-status?     false}]
     [sub-display/participants-list {:decision decision :field-configs (:template/fields template)}]
     [sub-display/reservations-list {:template template :decision decision}]
     [sub-display/attachments-list decision]]))

(defn- decisions-section
  [{:keys [entity-data template]}]
  (let [slots (tp-util/displayable-slots-by-category template entity-data :decisions)]
    [:div {:id "details-section-decisions" :class "mb-8"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900 mb-3"} (tr :decisions)]
     (if (seq slots)
       [:div {:class "flex flex-col gap-3"}
        (doall
         (for [{:keys [slot-kw entry instance]} slots]
           ^{:key slot-kw}
           [decision-detail-card {:entry entry :decision instance :template template}]))]
       [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-decision-types)])]))

(defn- attestation-detail-card
  [{:keys [entry attestation template]}]
  (let [outcome (:attestation/outcome attestation)
        slot-template (:slot/template entry)]
    [:div {:class (uu/cm "border rounded-lg p-4"
                         (case (tp-util/attestation-outcome-variant entry outcome)
                           :success "border-primary-regular/30 bg-primary-regular/5"
                           :warning "border-yellow-200 bg-yellow-50/50"
                           :danger  "border-red-200 bg-red-50/50"
                           "border-gray-200"))}
     [:div {:class "flex items-center justify-between mb-1"}
      [:span {:class "text-sm m-0 font-semibold text-gray-900"} (:slot/label entry)]
      (cond
        (not attestation)
        [:span {:class "text-xs m-0 text-gray-400 italic"} (tr :not-started)]

        (tp-util/attestation-display-label entry attestation)
        [tp-util/status-badge {:label   (tp-util/attestation-display-label entry attestation)
                               :variant (tp-util/attestation-outcome-variant entry outcome)}]

        :else
        [tp-util/render-error {:context "Unmapped :attestation/outcome keyword"
                               :value outcome}])]
     (when attestation
       (sub-display/value-field-rows {:slot-template slot-template :category :attestations
                                      :entity attestation :template template}))
     [sub-display/conditions-list {:entity           attestation
                                   :conditions-field :attestation/conditions
                                   :can-write?       false
                                   :show-status?     true}]
     [sub-display/attachments-list attestation]]))

(defn- attestations-section
  [{:keys [entity-data template]}]
  (let [slots (tp-util/displayable-slots-by-category template entity-data :attestations)]
    [:div {:id "details-section-attestations" :class "mb-8"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900 mb-3"} (tr :verifications)]
     (if (seq slots)
       [:div {:class "flex flex-col gap-3"}
        (doall
         (for [{:keys [slot-kw entry instance]} slots]
           ^{:key slot-kw}
           [attestation-detail-card {:entry entry :attestation instance :template template}]))]
       [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-attestation-types)])]))

(defn- declaration-detail-card
  [{:keys [entry declaration template]}]
  [:div {:class (uu/cm "border rounded-lg p-4"
                       (cond
                         (and declaration (:declaration/completed? declaration))
                         "border-primary-regular/30 bg-primary-regular/5"
                         (and declaration (:declaration/not-applicable? declaration))
                         "border-gray-200"
                         declaration
                         "border-yellow-200 bg-yellow-50/50"
                         :else "border-gray-200"))}
   [:div {:class "flex items-center justify-between mb-1"}
    [:span {:class "text-sm m-0 font-semibold text-gray-900"} (:slot/label entry)]
    (if declaration
      (let [[label variant] (cond
                              (:declaration/not-applicable? declaration)
                              [(tr :not-applicable) :neutral]
                              (:declaration/completed? declaration)
                              [(tr :completed) :success]
                              :else
                              [(tr :pending) :warning])]
        [tp-util/status-badge {:label label :variant variant}])
      [:span {:class "text-xs m-0 text-gray-400 italic"} (tr :pending)])]
   (when declaration
     (sub-display/value-field-rows {:slot-template (:slot/template entry) :category :declarations
                                    :entity declaration :template template}))])

(defn- declarations-section
  [{:keys [entity-data template]}]
  (let [slots (tp-util/displayable-slots-by-category template entity-data :declarations)]
    [:div {:id "details-section-declarations" :class "mb-8"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900 mb-3"} (tr :transaction-process/diligence-declarations)]
     (if (seq slots)
       [:div {:class "flex flex-col gap-3"}
        (doall
         (for [{:keys [slot-kw entry instance]} slots]
           ^{:key slot-kw}
           [declaration-detail-card {:entry entry :declaration instance :template template}]))]
       [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-declaration-types)])]))

(defn- document-stage-groups
  "Attachment slots grouped by stage, in template order, keeping only slots that have
  uploaded files. Grouping by stage disambiguates same-labelled slots (e.g. the
  per-stage 'Other documents') and mirrors the form's stage-specific document cards."
  [template attachments]
  (let [fields-config (:template/fields template)]
    (->> (:template/stages template)
         (keep (fn [stage]
                 (let [slots (->> (:stage/cards stage)
                                  (filter #(= :field-card (:card/type %)))
                                  (mapcat :card/fields)
                                  (keep (fn [fk]
                                          (let [fc (get fields-config fk)]
                                            (when (tp-util/attachment-slot-field? fc)
                                              (let [slot-atts (filter #(contains? (set (:common/system-kws %)) fk)
                                                                      attachments)]
                                                (when (seq slot-atts)
                                                  {:slot-id fk :slot-label (:field/label fc) :attachments slot-atts}))))))
                                  vec)]
                   (when (seq slots)
                     {:stage-label (:stage/label stage) :slots slots}))))
         vec)))

(defn- documents-section
  [{:keys [entity-data template]}]
  (let [attachments  (->> (:common/attachments entity-data)
                          (map (fn [ref] @(rf/subscribe [::session-sub/entity (or (:db/id ref) ref)])))
                          (remove nil?)
                          (remove :common/disabled?)
                          (sort-by :attachment/uploaded-on)
                          vec)
        stage-groups (document-stage-groups template attachments)]
    [:div {:id "details-section-documents" :class "mb-8"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900 mb-3"} (tr :documents)]
     (if (seq stage-groups)
       [:div {:class "flex flex-col gap-6"}
        (doall
         (for [{:keys [stage-label slots]} stage-groups]
           ^{:key stage-label}
           [:div
            [:h4 {:class "text-sm m-0 font-semibold text-gray-700 mb-2"} stage-label]
            [:div {:class "flex flex-col gap-4"}
             (doall
              (for [{:keys [slot-id slot-label] slot-atts :attachments} slots]
                ^{:key slot-id}
                [:div
                 [:h5 {:class "text-sm m-0 font-medium text-gray-700 mb-1"} slot-label]
                 [:div {:class "flex flex-col gap-1"}
                  (doall
                   (for [att slot-atts]
                     ^{:key (:db/id att)}
                     [attachment-row/view {:attachment att :read-only? true}]))]]))]]))]
       [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-documents)])]))

(defn- nav-sections []
  [{:id :details      :label (tr :details)}
   {:id :attestations :label (tr :verifications)}
   {:id :decisions    :label (tr :decisions)}
   {:id :declarations :label (tr :declarations)}
   {:id :documents    :label (tr :documents)}])

(defn- scroll-to-section!
  [section-id]
  (when-let [el (.getElementById js/document (str "details-section-" (name section-id)))]
    (.scrollIntoView el #js {:behavior "smooth" :block "start"})))

(defn view
  [{:keys [on-close entity-data template]}]
  [modal/view
   {:visible?      true
    :hide!         on-close
    :size          "1024px"
    :content-class "max-h-[85vh]"
    :body-class    "p-0 lg:p-0"}
   (tr :transaction-process/transaction-details)
   [:div {:class "flex flex-1 overflow-hidden"}
    [:div {:class "w-40 border-r border-gray-200 py-4 px-2 shrink-0 flex flex-col gap-1"}
     (for [section (nav-sections)]
       ^{:key (:id section)}
       [:button
        {:class    "text-left px-3 py-2 rounded text-sm m-0 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
         :on-click #(scroll-to-section! (:id section))}
        (:label section)])]
    [:div {:class "flex-1 overflow-auto p-6"}
     [details-section {:entity-data entity-data :template template}]
     [attestations-section {:entity-data entity-data :template template}]
     [decisions-section {:entity-data entity-data :template template}]
     [declarations-section {:entity-data entity-data :template template}]
     [documents-section {:entity-data entity-data :template template}]]]])
